import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Building2, HandCoins, UserRound } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db, type CachedDebt } from "../lib/db";
import { refreshFromServer } from "../lib/data";
import { applyLocalCash } from "../lib/local-operations";
import { Card, EmptyState, InlineNotice, Money, PageHeader, StatusBadge } from "../components/ui";

type Tab = "CUSTOMER" | "SUPPLIER";

export function DebtPage() {
  const auth = useAuth();
  const debts = useLiveQuery(async () => ((await db.meta.get("debts"))?.value as CachedDebt[] | undefined) ?? [], [], []);
  const [tab, setTab] = useState<Tab>("CUSTOMER");
  const [selected, setSelected] = useState<CachedDebt | null>(null);
  const [amount, setAmount] = useState(0);
  const [accountType, setAccountType] = useState<"CASH" | "BANK">("CASH");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const visible = useMemo(() => debts.filter((item) => item.counterpartyType === tab && item.balance > 0), [debts, tab]);
  const total = visible.reduce((sum, item) => sum + item.balance, 0);
  const role = auth.session?.user.role ?? "OWNER";
  const canSettle = ["OWNER", "MANAGER"].includes(role);
  const settlementAvailable =
    auth.demo ||
    (navigator.onLine && canSettle);

  function open(item: CachedDebt) {
    setSelected(item);
    setAmount(item.balance);
    setAccountType("CASH");
    setError("");
  }

  async function submit() {
    if (!selected || amount <= 0 || amount > selected.balance || !settlementAvailable) return;
    setBusy(true);
    setError("");
    const idempotencyKey = crypto.randomUUID();
    try {
      if (auth.demo) {
        const next = debts
          .map((item) => item.counterpartyType === selected.counterpartyType && item.counterpartyId === selected.counterpartyId
            ? { ...item, balance: Math.max(0, item.balance - amount), lastActivityAt: new Date().toISOString() }
            : item)
          .filter((item) => item.balance > 0);
        await db.meta.put({ key: "debts", value: next });
        await applyLocalCash(
          selected.counterpartyType === "CUSTOMER" ? "IN" : "OUT",
          amount,
          selected.counterpartyType === "CUSTOMER" ? "Thu công nợ" : "Trả công nợ NCC",
          accountType,
          idempotencyKey,
        );
      } else {
        await api("/debts/settle", {
          method: "POST",
          body: JSON.stringify({
            idempotencyKey,
            counterpartyType: selected.counterpartyType,
            counterpartyId: selected.counterpartyId,
            amount: Math.round(amount),
            accountType,
          }),
        });
        await refreshFromServer();
      }
      setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể xử lý công nợ");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader back title="Công nợ" description="Theo dõi khách nợ và khoản phải trả nhà cung cấp" />
      {!navigator.onLine && !auth.demo ? (
        <InlineNotice tone="warning" title="Cần kết nối để thu/trả công nợ">
          Số dư vẫn xem được ngoại tuyến; việc tất toán cần kiểm tra số dư authoritative trên server.
        </InlineNotice>
      ) : !canSettle && !auth.demo ? (
        <InlineNotice tone="info" title="Chỉ Chủ cửa hàng hoặc Quản lý được tất toán công nợ">
          Bạn vẫn có thể xem số dư và lịch sử công nợ.
        </InlineNotice>
      ) : null}
      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <div className="segmented">
        <button className={tab === "CUSTOMER" ? "active" : ""} onClick={() => setTab("CUSTOMER")}>Khách nợ</button>
        <button className={tab === "SUPPLIER" ? "active" : ""} onClick={() => setTab("SUPPLIER")}>Nợ nhà cung cấp</button>
      </div>

      <Card className="debt-summary">
        <span>{tab === "CUSTOMER" ? "Tổng cần thu" : "Tổng phải trả"}</span>
        <Money value={total} className="money-hero" />
        <small>{visible.length} đối tác còn số dư</small>
      </Card>

      {visible.length === 0 ? (
        <EmptyState title="Không có công nợ" description={tab === "CUSTOMER" ? "Không có khách hàng đang nợ." : "Không có khoản phải trả nhà cung cấp."} />
      ) : (
        <Card className="partner-list">
          {visible.map((item) => (
            <button className="debt-row" key={`${item.counterpartyType}-${item.counterpartyId}`} onClick={() => open(item)}>
              <span className="partner-icon">{item.counterpartyType === "CUSTOMER" ? <UserRound size={20} /> : <Building2 size={20} />}</span>
              <div><strong>{item.name}</strong><small>{item.phone || "Không có số điện thoại"}</small></div>
              <div className="debt-amount"><Money value={item.balance} /><StatusBadge tone="warning">{item.counterpartyType === "CUSTOMER" ? "Cần thu" : "Phải trả"}</StatusBadge></div>
            </button>
          ))}
        </Card>
      )}

      {selected ? (
        <div className="modal-backdrop" onMouseDown={() => !busy && setSelected(null)}>
          <div className="bottom-sheet" onMouseDown={(event) => event.stopPropagation()}>
            <div className="sheet-handle" />
            <div className="form-section-title"><HandCoins size={20} /><h2>{selected.counterpartyType === "CUSTOMER" ? "Thu nợ" : "Trả nợ"}</h2></div>
            <p><strong>{selected.name}</strong><br /><small>Số dư hiện tại: {selected.balance.toLocaleString("vi-VN")} ₫</small></p>
            <label>Số tiền<input className="currency-field" type="number" min="1" max={selected.balance} value={amount} onChange={(event) => setAmount(Number(event.target.value) || 0)} /></label>
            <label>Phương thức
              <select value={accountType} onChange={(event) => setAccountType(event.target.value as "CASH" | "BANK")}>
                <option value="CASH">Tiền mặt</option>
                <option value="BANK">Ngân hàng</option>
              </select>
            </label>
            <button className="btn btn-primary btn-lg" disabled={busy || !settlementAvailable || amount <= 0 || amount > selected.balance} onClick={() => void submit()}>
              {busy ? "Đang lưu..." : selected.counterpartyType === "CUSTOMER" ? "Xác nhận đã thu" : "Xác nhận đã trả"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
